import React from 'react';
import { FileText, Clock, CheckCircle, DollarSign } from 'lucide-react';

interface CreditStatsProps {
  stats: {
    totalApplications: number;
    pendingApproval: number;
    approved: number;
    rejected: number;
    totalAmount: number;
  };
}

export default function CreditStats({ stats }: CreditStatsProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const statCards = [
    {
      label: 'Total Aplikasi',
      value: stats.totalApplications,
      icon: FileText,
      color: 'blue',
      subtitle: null,
    },
    {
      label: 'Pending Approval',
      value: stats.pendingApproval,
      icon: Clock,
      color: 'amber',
      subtitle: 'menunggu keputusan',
      subtitleColor: 'text-amber-600',
    },
    {
      label: 'Disetujui',
      value: stats.approved,
      icon: CheckCircle,
      color: 'green',
      subtitle: `${stats.rejected} ditolak`,
      subtitleColor: 'text-red-600',
    },
    {
      label: 'Total Disetujui',
      value: formatCurrency(stats.totalAmount),
      icon: DollarSign,
      color: 'emerald',
      subtitle: null,
      isLarge: false,
    },
  ];

  const colorClasses: Record<string, { border: string; bg: string; icon: string }> = {
    blue: { border: 'border-l-blue-500', bg: 'bg-blue-50', icon: 'text-blue-600' },
    amber: { border: 'border-l-amber-500', bg: 'bg-amber-50', icon: 'text-amber-600' },
    green: { border: 'border-l-green-500', bg: 'bg-green-50', icon: 'text-green-600' },
    emerald: { border: 'border-l-emerald-500', bg: 'bg-emerald-50', icon: 'text-emerald-600' },
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        const colors = colorClasses[stat.color];
        return (
          <div
            key={stat.label}
            className={`bg-white border rounded-lg border-l-4 ${colors.border} p-4 animate-fadeIn`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p
                  className={`font-bold ${typeof stat.value === 'string' ? 'text-lg' : 'text-2xl'}`}
                >
                  {stat.value}
                </p>
                {stat.subtitle && (
                  <p className={`text-xs mt-1 ${stat.subtitleColor}`}>{stat.subtitle}</p>
                )}
              </div>
              <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${colors.icon}`} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
